import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { 
  Lightbulb, 
  FolderKanban, 
  Bot, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Sparkles,
  Crown,
  ScanSearch,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const debugPlanEnabled = localStorage.getItem("plan_override_enabled") === "true";
  const overridePlan = (localStorage.getItem("test_plan_override") || "").trim().toLowerCase();
  const hasAdminPin = Boolean((localStorage.getItem("admin_plan_pin") || "").trim());
  const canUsePlanOverride = debugPlanEnabled && hasAdminPin;
  const effectivePlan =
    canUsePlanOverride && ["free", "pro", "full_access"].includes(overridePlan)
      ? overridePlan
      : (user?.plan || "free").toLowerCase();
  const isRealAdmin = Boolean(user?.is_admin) || user?.role === "admin";
  const showAdminDebugPlan =
    isRealAdmin &&
    canUsePlanOverride &&
    ["free", "pro", "full_access"].includes(overridePlan) &&
    hasAdminPin;
  const planCapabilities =
    effectivePlan === "pro" || effectivePlan === "full_access"
      ? ["All opportunities", "All project features", "Cofounder (standard limits)"]
      : ["Browse all opportunities & Top 10 leaderboard", "Full detail views on the radar", "Cofounder locked"];

  const avatarSrc =
    user?.avatar_url ||
    user?.picture ||
    user?.user_metadata?.avatar_url ||
    user?.user_metadata?.picture ||
    user?.identities?.[0]?.identity_data?.avatar_url ||
    user?.identities?.[0]?.identity_data?.picture ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      user?.name || user?.email || "User"
    )}&backgroundType=gradientLinear`;

  const navItems = [
    { path: "/dashboard/opportunities", label: "Opportunities", icon: <Lightbulb className="w-5 h-5" /> },
    { path: "/dashboard/idea-check", label: "Idea checker", icon: <ScanSearch className="w-5 h-5" /> },
    { path: "/dashboard/projects", label: "My Projects", icon: <FolderKanban className="w-5 h-5" /> },
    { path: "/dashboard/ai-builder", label: "Cofounder", icon: <Bot className="w-5 h-5" />, proFeature: true },
    { path: "/dashboard/settings", label: "Settings", icon: <Settings className="w-5 h-5" /> }
  ];

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      // Slight delay to make the sign-out feedback feel intentional.
      await new Promise((resolve) => setTimeout(resolve, 900));
      await logout();
      navigate("/");
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex overflow-x-hidden max-w-[100vw]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#111111]/90 backdrop-blur-xl border-b border-[#1F1F1F] px-4 h-16 flex items-center justify-between pt-[env(safe-area-inset-top)]">
        <div className="flex items-center gap-2">
          {logoBroken ? (
            <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
          ) : (
            <img
              src="/logo.png"
              alt="SaaS-Radar"
              className="h-8 w-8 rounded-lg object-contain"
              onError={() => setLogoBroken(true)}
            />
          )}
          <span className="font-bold text-lg">SaaS-Radar</span>
        </div>
        <button
          className="text-zinc-400 hover:text-white p-2"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#111111] border-r border-[#1F1F1F] transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 hidden lg:flex items-center gap-2">
            {logoBroken ? (
              <div className="w-10 h-10 rounded-xl bg-[#7C3AED] flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-black" />
              </div>
            ) : (
              <img
                src="/logo.png"
                alt="SaaS-Radar"
                className="h-10 w-10 rounded-xl object-contain"
                onError={() => setLogoBroken(true)}
              />
            )}
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              SaaS-Radar
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 lg:py-0 mt-16 lg:mt-0 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 min-h-[48px] md:min-h-0 rounded-lg transition-all border-l-2
                  ${isActive 
                    ? 'text-white border-[#7C3AED] bg-[#7C3AED]/5' 
                    : 'text-zinc-500 border-transparent hover:text-zinc-300'
                  }
                `}
                onClick={(event) => {
                  setSidebarOpen(false);
                  if (item.path === "/dashboard/ai-builder" && effectivePlan === "free") {
                    event.preventDefault();
                    toast.message("Cofounder is a Pro feature. Upgrade to continue.");
                    navigate("/dashboard/settings/billing");
                  }
                }}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.icon}
                <span className="font-medium inline-flex items-center gap-1.5">
                  {item.label}
                  {item.proFeature && effectivePlan === "free" ? (
                    <Crown className="w-3.5 h-3.5 text-[#A78BFA]" />
                  ) : null}
                </span>
              </NavLink>
            ))}
          </nav>

          {/* Plan Badge */}
          {effectivePlan === "free" && (
            <div className="mx-4 mb-4 p-4 rounded-2xl bg-gradient-to-r from-[#7C3AED]/10 to-[#6D28D9]/10 border border-[#7C3AED]/20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#7C3AED]">Upgrade to Pro</span>
              </div>
              <p className="text-xs text-zinc-400 mb-3">Unlock unlimited AI access and advanced analytics</p>
              <Button 
                className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold rounded-lg py-2"
                onClick={() => navigate("/dashboard/settings/billing")}
                data-testid="upgrade-btn"
              >
                Upgrade Now
              </Button>
            </div>
          )}

          {/* User Section */}
          <div className="p-4 border-t border-[#1F1F1F]">
            {showAdminDebugPlan && (
              <div className="mb-3 rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/10 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wider text-zinc-400">Mode actif</p>
                <p className="text-sm font-semibold text-[#C4B5FD]">{effectivePlan}</p>
                <p className="text-[11px] text-zinc-400 mt-1">{planCapabilities[0]}</p>
                <p className="text-[11px] text-zinc-400">{planCapabilities[1]}</p>
                <p className="text-[11px] text-zinc-400">{planCapabilities[2]}</p>
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={avatarSrc} alt={user?.name} />
                <AvatarFallback className="bg-[#111111] text-white">
                  {user?.name?.split(" ").map((part) => part[0]).join("").slice(0, 2) || user?.email?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-zinc-500 hover:text-white hover:bg-white/5"
              onClick={() => setShowLogoutConfirm(true)}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:h-screen lg:overflow-y-auto overflow-x-hidden pt-16 pb-[calc(2rem+env(safe-area-inset-bottom))] lg:pb-0 lg:pt-0">
        <Outlet />
      </main>

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/65 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#2A2A2A] bg-[#111111] p-6 shadow-[0_20px_70px_rgba(0,0,0,0.5)]">
            <h3
              className="text-xl font-semibold text-white mb-2"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Confirm sign out
            </h3>
            <p className="text-sm text-zinc-400 mb-6">
              You are about to leave your current session. Do you want to sign out now?
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                className="text-zinc-400 hover:text-white hover:bg-white/5"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
              >
                Cancel
              </Button>
              <Button
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white min-w-[130px]"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                    Signing out...
                  </span>
                ) : (
                  "Yes, sign out"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
